import { useMemo } from 'react';
import { Modal, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { aboveEverythingZIndex } from '@/shared/ui/FloatingWindow';
import type { PreviewAffectedDocument, PreviewBalanceChange, PreviewDiff, PreviewPaymentChange, PreviewStockChange } from '@/entities/document';
import { affectedNotes, paymentChangeText } from './model';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };

export interface PreviewConsequencesDialogProps {
  diff: PreviewDiff | undefined;
  /** Предупреждения, с которыми документ будет сохранён, — уже словами (названия товаров знает
   * только окно документа). */
  warningTexts?: string[];
  onResolve: (confirmed: boolean) => void;
}

const stockColumns: ColumnsType<PreviewStockChange> = [
  { title: 'Товар', dataIndex: 'categoryName' },
  { title: 'Было', width: 100, align: 'right', render: (_v, r) => r.before.qtyTotal },
  { title: 'Станет', width: 100, align: 'right', render: (_v, r) => r.after.qtyTotal },
];

const documentColumns: ColumnsType<PreviewAffectedDocument> = [
  { title: 'Документ', dataIndex: 'label', width: 120 },
  { title: 'Сумма', width: 160, render: (_v, r) => `${r.totalAmountBefore} → ${r.totalAmountAfter}` },
  { title: 'Себестоимость', width: 160, render: (_v, r) => `${r.costBefore} → ${r.costAfter}` },
  {
    title: 'Статус оплаты',
    width: 160,
    render: (_v, r) => `${PAYMENT_STATUS_LABEL[r.paymentStatusBefore] ?? r.paymentStatusBefore} → ${PAYMENT_STATUS_LABEL[r.paymentStatusAfter] ?? r.paymentStatusAfter}`,
  },
  { title: 'Что ещё изменится', render: (_v, r) => affectedNotes(r).join('; ') || '—' },
];

const paymentColumns: ColumnsType<PreviewPaymentChange> = [
  { title: 'Платёж', dataIndex: 'label' },
  { title: 'Контрагент', dataIndex: 'counterpartyName', width: 180 },
  { title: 'Что произойдёт', width: 260, render: (_v, r) => paymentChangeText(r) },
];

const balanceColumns: ColumnsType<PreviewBalanceChange> = [
  { title: 'Контрагент', dataIndex: 'counterpartyName' },
  { title: 'Было', width: 120, align: 'right', render: (_v, r) => r.before },
  { title: 'Станет', width: 120, align: 'right', render: (_v, r) => r.after },
];

/** §8.5: «Остаток товара X станет −10 шт» / «Затронуты документы...» / «Баланс контрагента Y:
 * было → станет» — предупреждение, а не блокировка (кнопки «Сохранить»/«Отмена»). */
export function PreviewConsequencesDialog({ diff, warningTexts = [], onResolve }: PreviewConsequencesDialogProps) {
  const isOpen = diff !== undefined;
  // Плавающие окна документов используют собственный растущий z-index (shared/ui/FloatingWindow) —
  // выше базового antd-модального (1000), поэтому обычный Modal рисуется ПОД открытым окном
  // документа. Берём следующий z-index из того же счётчика при каждом открытии диалога, чтобы
  // он гарантированно оказался поверх любого уже открытого окна.
  const zIndex = useMemo(() => (isOpen ? aboveEverythingZIndex() : undefined), [isOpen]);

  return (
    <Modal
      title="Последствия изменения"
      open={isOpen}
      onOk={() => onResolve(true)}
      onCancel={() => onResolve(false)}
      okText="Сохранить"
      cancelText="Отмена"
      width={860}
      zIndex={zIndex}
    >
      {warningTexts.length > 0 ? (
        <>
          <Typography.Text strong>Предупреждения</Typography.Text>
          <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 18 }}>
            {warningTexts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </>
      ) : null}
      {diff && diff.stockChanges.length > 0 ? (
        <>
          <Typography.Text strong>Остатки товаров</Typography.Text>
          <Table<PreviewStockChange> size="small" rowKey="categoryId" dataSource={diff.stockChanges} columns={stockColumns} pagination={false} style={{ marginBottom: 16 }} />
        </>
      ) : null}
      {diff && diff.affectedDocuments.length > 0 ? (
        <>
          <Typography.Text strong>Затронутые документы</Typography.Text>
          <Table<PreviewAffectedDocument>
            size="small"
            rowKey="documentId"
            dataSource={diff.affectedDocuments}
            columns={documentColumns}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
        </>
      ) : null}
      {diff && diff.balances.length > 0 ? (
        <>
          <Typography.Text strong>Баланс контрагентов</Typography.Text>
          <Table<PreviewBalanceChange>
            size="small"
            rowKey="counterpartyId"
            dataSource={diff.balances}
            columns={balanceColumns}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
        </>
      ) : null}
      {diff && diff.payments.length > 0 ? (
        <>
          <Typography.Text strong>Платежи</Typography.Text>
          <Table<PreviewPaymentChange> size="small" rowKey="paymentId" dataSource={diff.payments} columns={paymentColumns} pagination={false} />
        </>
      ) : null}
    </Modal>
  );
}
